import React from "react";
import { Step } from "semantic-ui-react";
import { Wizard } from "@redriver/cinnamon";
import { FunderType } from "features/../../../shared/constants/enums";
import { useTranslation } from "react-i18next";
import FunderCFA from "./FunderCFA";
import DealerCFA from "./DealerCFA";
import CoFundedCFA from "./CoFundedCFA";
import { CalcTableType } from "mortar/Principal/Enums";
import { useSheetCalcInfo } from "features/Sheets";

const FunderTypeTabs = ({ data }) => {
  const { t } = useTranslation("capitalCalc", {
    keyPrefix: "infoModal.funderTypes",
  });
  const { isMssa } = useSheetCalcInfo();

  const getSummary = (funderType, calcTableType) =>
    data?.funderSummaries?.find(
      (x) => x.funderType === funderType && x.calcTableType === calcTableType,
    );

  const createStep = (funderType, calcTableType) => ({
    key: `${funderType}_${calcTableType}`,
    titleKey: isMssa ? `${funderType}_${calcTableType}` : funderType,
  });

  const stepsAndData = [
    FunderType.Funder,
    FunderType.Dealer,
    FunderType.CoFunded,
  ]
    .map((funderType) =>
      [CalcTableType.SoftwareAssignment, CalcTableType.Standard].map(
        (calcTableType) => ({
          funderType,
          calcTableType,
          step: createStep(
            funderType,
            calcTableType,
            `${funderType}_${calcTableType}`,
          ),
          data: getSummary(funderType, calcTableType),
        }),
      ),
    )
    .flat()
    .filter((x) => !!x.data);

  const steps = stepsAndData.map(({ step }) => step);

  const getBreakdownData = (funderType, calcTableType) =>
    stepsAndData.find(
      (x) => x.funderType === funderType && x.calcTableType === calcTableType,
    )?.data;

  const NavigationControls = ({ steps, activeStep, onGoToStep }) => (
    <Step.Group ordered={false}>
      {steps.map((step) => (
        <Step
          key={step.key}
          active={step === activeStep}
          content={t(step.titleKey)}
          onClick={() => onGoToStep(step.key)}
        />
      ))}
    </Step.Group>
  );

  return (
    <div className="funder-types">
      <Wizard steps={steps}>
        <Wizard.Nav component={NavigationControls} />
        {stepsAndData.map(({ funderType, calcTableType }) => (
          <Wizard.Step
            key={`${funderType}_${calcTableType}`}
            step={`${funderType}_${calcTableType}`}
            render={() => {
              switch (funderType) {
                case FunderType.Funder:
                  return (
                    <FunderCFA
                      breakdown={getBreakdownData(funderType, calcTableType)}
                    />
                  );
                case FunderType.Dealer:
                  return (
                    <DealerCFA
                      breakdown={getBreakdownData(funderType, calcTableType)}
                    />
                  );
                case FunderType.CoFunded:
                  return (
                    <CoFundedCFA
                      breakdown={getBreakdownData(funderType, calcTableType)}
                    />
                  );
                default:
                  return null;
              }
            }}
          />
        ))}
      </Wizard>
    </div>
  );
};

export default FunderTypeTabs;
